import { channelsInstance } from '@/api/instances'
import { ChannelStatus } from '@/models/channels'
import { CHANNEL_SEGMENT } from '@/models/virtualClinic'
import { getCounters } from '@/utils/channel'
import DatabaseFactory from '@/wrappers/database'
import { addErrorLog } from '@/wrappers/database/utils/addLog'

const getStaffs = async (store, increment = 10) => {
  const limit = store.state.currentPage * increment

  const database = new DatabaseFactory('staff').getReadDatabase()

  const query = {
    limit,
    orderBy: [],
    filters: []
  }
  const sort = store.state.sort

  query.orderBy = addSortToQuery(sort, store.state.staffFilter)
  query.filters = addFilterToQuery(
    store.state.staffFilter,
    store.state.staffRoles
  )
  let collection = {}
  try {
    collection = await database.get(query)
  } catch (error) {
    addErrorLog({
      context: 'dashboard/actions - getStaffs',
      error,
      query
    })
    return
  }
  const staffs = collection.docs.map(doc => getStaffCompiled(doc.data()))
  store.commit('setStaffs', staffs)
  store.dispatch('getDashboardDigest')

  if (staffs.length < limit) {
    store.commit('setEndStaffResults', true)
  }
}

/**
 * Get pagination sort and mount orderBy query
 * The sort object in store have the same fields from firestore
 * Order only if dont have filter (firestore limitation)
 * @param {Object} sort - status, name
 * @param {Object} staffFilter
 * @returns Order Array
 */
const addSortToQuery = (sort, staffFilter) => {
  const orderBy = []
  if (staffFilter.id) {
    //If filter by staffId dont need to do query with sort
    return orderBy
  }
  for (const key in sort) {
    if (sort[key] && !staffFilter[key]) {
      orderBy.push({
        field: key,
        sort: sort[key]
      })
    }
  }
  return orderBy
}

/**
 * Get pagination filter and mount query
 * The filter object in store have the same fields from firestore
 *
 * If dont have role filter set or filter by id, initialize filter with the roles from
 * staffRoles (alice agora roles), with this set, dont get all staffs
 * from firestore
 * @param {Object} staffFilter
 * @param {Array} roles
 * @returns Filters Array
 */
const addFilterToQuery = (staffFilter, roles) => {
  const filters = []
  for (const key in staffFilter) {
    const value = staffFilter[key]
    if (value) {
      filters.push({
        field: key,
        condition: Array.isArray(value) ? 'in' : '==',
        value: value
      })
    }
  }
  if (
    !staffFilter.role &&
    !staffFilter.id &&
    roles?.length > 0 &&
    roles?.length <= 10
  ) {
    //firestore limitation: cant do query with more than 10 itens array
    const rolesId = roles.map(role => role.name)
    filters.push({
      field: 'role',
      condition: 'in',
      value: rolesId
    })
  }
  return filters
}

const setMonitoredChannel = async (store, channelId) => {
  try {
    await channelsInstance.post(`channel_monitoring/${channelId}`)
    return channelId
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/setMonitoredChannel' },
      { root: true }
    )
  }
}

const getStaffChannels = async (store, { staffId, kind }) => {
  const database = new DatabaseFactory(`channels`).getReadDatabase()
  const query = {
    filters: [
      {
        field: 'status',
        condition: '==',
        value: ChannelStatus.ACTIVE
      },
      {
        field: 'staffIds',
        condition: 'array-contains',
        value: staffId
      },
      {
        field: 'kind',
        condition: '==',
        value: kind
      }
    ]
  }

  let collection = {}
  try {
    collection = await database.get(query)
  } catch (error) {
    addErrorLog({
      context: 'dashboard/actions - getStaffChannels',
      error
    })
    return
  }
  const channels = collection.docs.map(doc => {
    const channel = doc.data()
    return {
      id: doc.id,
      ...getChannelCompiled(channel)
    }
  })

  store.commit('setStaffChannels', channels)
}

const getDashboardDigest = async store => {
  try {
    const data = await channelsInstance.get('channels/dashboard/counters')
    const queue = await store.dispatch('getVirtualClinicQueue')
    store.commit('setDashboardDigest', {
      ...data,
      ...queue
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        message: 'Não foi possível obter os contadores',
        error
      },
      { root: true }
    )
  }
}

const getVirtualClinicQueue = async () => {
  const database = new DatabaseFactory('virtual_clinic_queue').getReadDatabase()

  try {
    const snapshot = await database.get()
    const queue = snapshot?.docs.map(doc => doc.data())

    if (!queue) {
      return null
    }

    const adultQueue =
      queue.filter(item => item.segment === CHANNEL_SEGMENT.ADULT)?.length || 0
    return {
      virtualClinicQueue: queue.length,
      adultQueue,
      pediatricQueue: queue.length - adultQueue
    }
  } catch (error) {
    addErrorLog({
      context: 'dashboard/actions - getVirtualClinicQueue',
      error
    })
    return null
  }
}

const getStaffRoles = async store => {
  const roles = await channelsInstance.get('channels/alice_agora/roles')

  store.commit('setStaffRoles', roles)
}

export const getStaffCompiled = staff => {
  const staffCompiled = {
    id: staff.id,
    name: staff.name,
    description: staff.description,
    photo: staff.profileImageUrl,
    status: staff.status
  }
  if (!staff.counters?.sections) {
    return {
      ...staffCompiled,
      totalChats: 0,
      totalChannels: 0,
      channelIds: []
    }
  }
  const channelCounters = getCounters(staff.counters.sections.channels)
  const chatCounters = getCounters(staff.counters.sections.chats)
  const totalChats = chatCounters.total + chatCounters.totalHighlighted
  const totalChannels = channelCounters.total + channelCounters.totalHighlighted

  const channelIds = []
  channelIds.push(staff.counters.sections.chats.total)
  channelIds.push(staff.counters.sections.channels.total)

  return {
    ...staffCompiled,
    totalChats,
    totalChannels,
    total: totalChannels + totalChats,
    channelIds: channelIds.flat()
  }
}

const getChannelCompiled = channel => {
  return {
    ...channel,
    createdAt: channel.createdAt ? channel.createdAt.toDate() : null
  }
}

const incrementCurrentPage = store => {
  store.commit('incrementCurrentPage')
}

const addFilter = (store, filter) => {
  store.commit('resetCurrentPage')
  store.commit('setEndStaffResults', false)
  store.commit('addFilter', filter)
}

const addSort = (store, sort) => {
  store.commit('addSort', sort)
}

const setStaffs = (store, staffs) => {
  store.commit('setStaffs', staffs)
}

const clearState = store => {
  store.commit('clearState')
}

const initPaginationState = store => {
  store.commit('initPaginationState')
}

const clearStaffChannels = store => {
  store.commit('clearStaffChannels')
}

export default {
  clearState,
  initPaginationState,
  getStaffChannels,
  incrementCurrentPage,
  getDashboardDigest,
  getStaffs,
  setStaffs,
  addFilter,
  getStaffRoles,
  addSort,
  clearStaffChannels,
  getVirtualClinicQueue,
  setMonitoredChannel
}
